import { useThemeColors } from '@/hooks/useThemeColors';
import { CheckCircle, Play } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

export interface ClassData {
    id: number;
    title: string;
    description?: string;
    image?: string;
    order: number;
    is_completed?: boolean;
    coming_soon?: boolean;
    topic: {
        id: number;
        title: string;
    };
}

interface ClassCardProps {
    classData: ClassData;
    classNumber: number;
    onClick?: () => void;
}

const ClassCard: React.FC<ClassCardProps> = ({ classData, classNumber, onClick }) => {
    const { t } = useTranslation();
    const colors = useThemeColors();
    const isCompleted = classData.is_completed || false;
    const isComingSoon = classData.coming_soon || false;

    return (
        <div
            className={`group relative overflow-hidden rounded-xl border transition-all duration-200 ${
                isComingSoon
                    ? `cursor-default opacity-80 ${colors.border} ${colors.accentBg}`
                    : isCompleted
                      ? 'cursor-pointer border-green-200 bg-green-50 hover:border-gray-300'
                      : 'cursor-pointer border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 hover:border-gray-300 dark:hover:border-neutral-600'
            }`}
            onClick={isComingSoon ? undefined : onClick}
        >
            {/* Subtle top accent */}
            <div
                className={`h-1 ${isComingSoon ? colors.primaryBg : isCompleted ? 'bg-green-500' : colors.primaryBg}`}
            ></div>

            <div className="p-6">
                {/* Class number badge */}
                <div className="mb-4 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div
                            className={`flex h-8 w-8 items-center justify-center rounded-full ${
                                isComingSoon ? colors.iconBg : isCompleted ? 'bg-green-500' : colors.primaryBg
                            }`}
                        >
                            {isCompleted ? (
                                <CheckCircle size={16} className="text-white" />
                            ) : (
                                <Play
                                    size={14}
                                    className={isComingSoon ? colors.iconColor : 'text-white'}
                                    fill="currentColor"
                                />
                            )}
                        </div>
                        <span
                            className={`text-sm font-semibold ${isComingSoon ? colors.iconColor : isCompleted ? 'text-green-700' : colors.iconColor}`}
                        >
                            {t('course.class')} {classNumber}
                        </span>
                    </div>
                    {isComingSoon ? (
                        <span className={`rounded-full px-2 py-1 text-xs font-medium ${colors.badgeBg} ${colors.badgeText}`}>
                            {t('course.comingSoon')}
                        </span>
                    ) : isCompleted ? (
                        <span className="rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-600">
                            {t('course.completed')}
                        </span>
                    ) : null}
                </div>

                {/* Title */}
                <h3
                    className={`text-sm leading-snug font-semibold ${isComingSoon ? colors.accentText : isCompleted ? 'text-green-800' : 'text-gray-900 dark:text-white'}`}
                >
                    {classData.title}
                </h3>
            </div>
        </div>
    );
};

export default ClassCard;
